import { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { config } from '../config.js';
import { useAuth } from '../auth/useAuth.js';
import { useUiStore } from '../state/uiStore.js';
import { listTenants } from '../api/tenants.js';
import { t } from '../i18n/es.js';

export function Topbar(): JSX.Element {
  const { claims, logout } = useAuth();
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const activeTenantId = useUiStore((s) => s.activeTenantId);
  const setActiveTenant = useUiStore((s) => s.setActiveTenant);
  const navigate = useNavigate();
  const { id: pathTenantId } = useParams<{ id?: string }>();

  const { data } = useQuery({
    queryKey: ['tenants', 'topbar'],
    queryFn: () => listTenants({ limit: 50 }),
    staleTime: 60_000,
  });
  const tenants = data?.items ?? [];

  // Match path param contra id Y slug. NO se sincroniza al store (las pages
  // hacen `setActiveTenant(pathTenantId)` con el slug; intentar resolverlo a
  // UUID acá provoca loop con las pages). El dropdown calcula su value en
  // runtime priorizando resolvedId sobre el store.
  const resolvedId = useMemo<string | undefined>(() => {
    if (!pathTenantId) return undefined;
    const found = tenants.find((tn) => tn.id === pathTenantId || tn.slug === pathTenantId);
    return found?.id ?? pathTenantId;
  }, [pathTenantId, tenants]);

  // Sincroniza path crudo → store solo si las pages no lo hicieron todavía.
  useEffect(() => {
    if (pathTenantId && pathTenantId !== activeTenantId) {
      setActiveTenant(pathTenantId);
    }
  }, [pathTenantId, activeTenantId, setActiveTenant]);

  const dropdownValue = resolvedId ?? activeTenantId ?? '';

  function onSelect(e: React.ChangeEvent<HTMLSelectElement>): void {
    const id = e.target.value || null;
    setActiveTenant(id);
    if (id) navigate(`/tenants/${id}`);
  }

  return (
    <header className="flex h-14 items-center justify-between border-b border-slate-200 bg-white px-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label="toggle sidebar"
          onClick={toggleSidebar}
          className="rounded p-2 hover:bg-slate-100"
        >
          ≡
        </button>
        <h1 className="text-base font-semibold">{t.app.title}</h1>
        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs uppercase tracking-wide text-slate-600">
          {config.envLabel}
        </span>
      </div>
      <div className="flex items-center gap-3 text-sm">
        <label className="flex items-center gap-2">
          <span className="text-xs text-slate-500">{t.topbar.activeTenant}</span>
          <select
            aria-label={t.topbar.activeTenant}
            value={dropdownValue}
            onChange={onSelect}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            <option value="">{t.topbar.selectTenant}</option>
            {tenants.map((tn) => (
              <option key={tn.id} value={tn.id}>
                {tn.name} ({tn.slug})
              </option>
            ))}
          </select>
        </label>
        {claims?.email && <span className="text-slate-600">{claims.email}</span>}
        <button
          type="button"
          onClick={logout}
          className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-slate-100"
        >
          {t.app.logout}
        </button>
      </div>
    </header>
  );
}
